// client/src/components/pages/LexiStar/Reports/dailyLogic.ts
//
// The daily management summary's figure plan (ST-00188 M4-R2; FR-RPT-001 for
// one day, FR-RPT-005 briefing). The metric keys are M4-A's metric pack
// (server/modules/lexistar/analytics/pack); the page asks the KPI API only for
// the keys the caller's catalogue (GET /metrics, permission-filtered) holds,
// and prints whatever comes back, section by section, against the previous day.

import type { Better } from '../Dashboard/metricFormat';

export interface FigureSpec {
  key: string;
  /** Which way is better (words and arrows only — never red/green). */
  better: Better;
}

export interface FigureSection {
  id: string;
  title: string;
  figures: FigureSpec[];
}

const f = (key: string, better: Better): FigureSpec => ({ key, better });

/** FR-RPT-001 in the order a manager reads it. */
export const DAILY_SECTIONS: FigureSection[] = [
  {
    id: 'trips',
    title: 'Trips',
    figures: [
      f('trip.planned', 'neutral'),
      f('trip.started', 'neutral'),
      f('trip.completed', 'up'),
      f('trip.late', 'down'),
      f('trip.cancelled', 'down'),
      f('trip.on_time_pct', 'up'),
    ],
  },
  {
    id: 'tonnes',
    title: 'Tonnes against plan',
    figures: [
      f('trip.tonnes_planned', 'neutral'),
      f('trip.tonnes_dispatched', 'up'),
      f('trip.tonnes_received', 'up'),
      f('trip.tonnes_moved', 'up'),
    ],
  },
  {
    id: 'route',
    title: 'On route and deviations',
    figures: [
      f('route.on_route_compliance_pct', 'up'),
      f('route.deviations_approved', 'neutral'),
      f('route.deviations_unapproved', 'down'),
      f('route.off_route_km', 'down'),
      f('trip.above_route_norm', 'down'),
    ],
  },
  {
    id: 'fuel',
    title: 'Fuel — allowance against actual',
    figures: [
      f('fuel.budget_l', 'neutral'),
      f('fuel.consumed_l', 'neutral'),
      f('trip.fuel_variance_l', 'up'),
      f('fuel.variance_zar', 'up'),
      f('fuel.l_per_100km', 'down'),
      f('fuel.overconsumption_trips', 'down'),
    ],
  },
  {
    id: 'idle',
    title: 'Idle',
    figures: [
      f('asset.idle_hours', 'down'),
      f('asset.idle_hours_elsewhere', 'down'),
      f('asset.idle_fuel_l', 'down'),
      f('asset.idle_cost_zar', 'down'),
    ],
  },
  {
    id: 'fleet',
    title: 'Fleet use',
    figures: [
      f('asset.utilisation_pct', 'up'),
      f('asset.availability_pct', 'up'),
      f('run.km_loaded', 'up'),
      f('run.km_empty', 'down'),
      f('run.empty_running_pct', 'down'),
    ],
  },
  {
    id: 'cycle',
    title: 'Cycle and dwell',
    figures: [
      f('lane.avg_cycle_min', 'down'),
      f('site.avg_dwell_loading_min', 'down'),
      f('site.avg_dwell_offloading_min', 'down'),
      f('site.avg_dwell_waiting_min', 'down'),
      f('site.avg_dwell_weighbridge_min', 'down'),
      f('site.dwell_exceedances', 'down'),
    ],
  },
  {
    id: 'weighbridge',
    title: 'Weighbridge',
    figures: [
      f('tickets.received', 'neutral'),
      f('tickets.matched_pct', 'up'),
      f('tickets.without_presence', 'down'),
      f('tickets.mass_variance_breaches', 'down'),
    ],
  },
  {
    id: 'cost',
    title: 'Estimated cost',
    figures: [f('cost.total_zar', 'neutral'), f('cost.per_t_zar', 'down'), f('comms.messages_cost_zar', 'down')],
  },
];

/** The stat tiles across the top (the figures a manager reads first). */
export const HEADLINE_KEYS = [
  'trip.completed',
  'trip.tonnes_moved',
  'route.on_route_compliance_pct',
  'trip.fuel_variance_l',
  'asset.idle_hours',
  'asset.utilisation_pct',
];

export const ALL_DAILY_KEYS = DAILY_SECTIONS.flatMap((s) => s.figures.map((x) => x.key));

export const betterOf = (key: string): Better =>
  DAILY_SECTIONS.flatMap((s) => s.figures).find((x) => x.key === key)?.better ?? 'neutral';

// ── Days (SAST calendar days, YYYY-MM-DD) ───────────────────────────────────

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isDay = (s: unknown): s is string => typeof s === 'string' && DAY_RE.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));

/** `day` ± n days. */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Today in SAST. */
export function todaySast(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg' }).format(now);
}

export const yesterdaySast = (now: Date = new Date()): string => addDays(todaySast(now), -1);

/** "▲ +12%" · "▼ −3.1 pts" · "no change" with a better/worse word. */
export function changeWords(delta: { text: string; direction: 'up' | 'down' | 'flat'; judgement: 'better' | 'worse' | null } | null): string {
  if (!delta) return '—';
  const arrow = delta.direction === 'up' ? '▲ ' : delta.direction === 'down' ? '▼ ' : '';
  return `${arrow}${delta.text}${delta.judgement ? ` · ${delta.judgement}` : ''}`;
}
